"use client";

import { useEffect, useId, useRef } from "react";
import { Search } from "../lib/icons";
import { formatProductIdentity } from "./product-identity";

export interface MarketplaceLookupProduct {
  id: string;
  name: string;
  vendor: string;
}

export interface MarketplaceProductLookupProps {
  expanded: boolean;
  query: string;
  results: MarketplaceLookupProduct[];
  status: "idle" | "loading" | "error";
  /** Explains why an otherwise successful search returned no selectable rows. */
  emptyMessage?: string;
  onExpandedChange: (expanded: boolean) => void;
  onQueryChange: (query: string) => void;
  onSelect: (product: MarketplaceLookupProduct) => void;
}

export function MarketplaceProductLookup({
  expanded,
  query,
  results,
  status,
  emptyMessage,
  onExpandedChange,
  onQueryChange,
  onSelect,
}: MarketplaceProductLookupProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const contentId = useId();
  const listboxId = useId();

  useEffect(() => {
    if (expanded) {
      inputRef.current?.focus();
    }
  }, [expanded]);

  const statusMessage =
    status === "loading"
      ? "Searching…"
      : status === "error"
        ? "Marketplace search is unavailable. Try again."
        : query.trim() && results.length === 0
          ? (emptyMessage ?? "No Marketplace products found.")
          : "";

  const collapse = () => {
    onExpandedChange(false);
    triggerRef.current?.focus();
  };

  const select = (product: MarketplaceLookupProduct) => {
    onSelect(product);
    triggerRef.current?.focus();
  };

  return (
    <div className="border-t border-(--border-subtle) bg-(--bg-white) px-[15px] py-[13px] font-[family-name:var(--font-body)]">
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={expanded}
        aria-controls={contentId}
        onClick={() => onExpandedChange(!expanded)}
        className="flex w-full cursor-pointer items-center gap-2 border-0 bg-transparent p-0 text-left text-[13px] font-semibold text-(--fg-brand) hover:text-(--fg-primary)"
      >
        <Search size={15} />
        Don’t see the product? Search Marketplace
      </button>

      {expanded && (
        <section
          id={contentId}
          aria-label="Marketplace product search"
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              collapse();
            }
          }}
          className="mt-3 flex flex-col gap-2"
        >
          <div className="flex items-center gap-2 rounded-(--radius-field) border border-(--border-default) bg-(--bg-white) px-[11px] py-[9px] focus-within:border-(--fg-brand) focus-within:shadow-[0_0_0_1px_var(--fg-brand)]">
            <span className="shrink-0 text-(--fg-muted)">
              <Search size={15} />
            </span>
            <input
              ref={inputRef}
              type="search"
              aria-label="Search Marketplace products"
              aria-controls={listboxId}
              value={query}
              onChange={(event) => onQueryChange(event.target.value)}
              placeholder="Search Marketplace products"
              className="min-w-0 flex-1 border-0 bg-transparent text-[13px] text-(--fg-primary) outline-none placeholder:text-(--fg-muted)"
            />
          </div>

          <div
            id={listboxId}
            role="listbox"
            aria-label="Marketplace products"
            className="flex flex-col gap-1.5"
          >
            {results.map((product) => (
              <button
                key={product.id}
                type="button"
                role="option"
                aria-selected="false"
                aria-label={formatProductIdentity(product)}
                onClick={() => select(product)}
                className="flex w-full cursor-pointer items-center gap-3 rounded-(--radius-field) border border-(--border-subtle) bg-(--bg-white) px-[11px] py-[9px] text-left hover:border-(--border-default) hover:bg-(--bg-canvas)"
              >
                <span
                  aria-hidden="true"
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-(--radius-sm) bg-(--bg-sky) text-[13px] font-semibold text-(--fg-brand)"
                >
                  {product.name.charAt(0).toUpperCase()}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-[13px] font-semibold text-(--fg-primary)">
                    {product.name}
                  </span>
                  <span className="block truncate text-[11.5px] text-(--fg-muted)">
                    {product.vendor}
                  </span>
                </span>
              </button>
            ))}
          </div>

          <p
            role="status"
            aria-live="polite"
            aria-atomic="true"
            className="min-h-4 text-[12px] text-(--fg-secondary)"
          >
            {statusMessage}
          </p>
        </section>
      )}
    </div>
  );
}
